import type { NativeEvent } from './sse'

export interface NativeRecallItem {
  text: string
  phase: string
  sequence?: number
}

export interface NativeContentSegment {
  kind: 'thinking' | 'text'
  content: string
  messageId?: string
  blockIndex?: number
}

export interface NativeTurnStreamState {
  // Compatibility aggregates. Ordered rendering must use segments when present.
  thinking: string
  text: string
  segments: NativeContentSegment[]
  recall: NativeRecallItem[]
  pendingText: string
  completedTextAwaitingDelta: string
  completedReplayProbe: string
  completedReplayMessageId: string
  completedMessageIds: string[]
  activeStreamMessageId: string
  activeSegmentStart: number
  completedReplayProbeBlockIndex: number | null
  completedReplayProbeStreamMessageId: string
}

export function emptyNativeTurnStream(): NativeTurnStreamState {
  return {
    thinking: '', text: '', segments: [], recall: [], pendingText: '',
    completedTextAwaitingDelta: '', completedReplayProbe: '', completedReplayMessageId: '',
    completedMessageIds: [], activeStreamMessageId: '', activeSegmentStart: 0,
    completedReplayProbeBlockIndex: null,
    completedReplayProbeStreamMessageId: '',
  }
}

export function nativeEventBelongsToTurn(event: NativeEvent, turnId: string): boolean {
  return !(event.turn_id && turnId && event.turn_id !== turnId)
}

function appendSegment(
  current: NativeTurnStreamState,
  kind: NativeContentSegment['kind'],
  content: string,
  options: { messageId?: string; blockIndex?: number } = {},
): NativeTurnStreamState {
  if (!content) return current
  const messageId = options.messageId || current.activeStreamMessageId || undefined
  const blockIndex = typeof options.blockIndex === 'number' ? options.blockIndex : undefined
  const segments = [...current.segments]
  const last = segments[segments.length - 1]
  const lastIsCurrentMessage = segments.length > 0 && segments.length - 1 >= current.activeSegmentStart
  const sameBlock = lastIsCurrentMessage && blockIndex !== undefined
    && last?.kind === kind
    && last.blockIndex === blockIndex
    && last.messageId === messageId
  const legacyAdjacent = lastIsCurrentMessage && blockIndex === undefined
    && last?.kind === kind
    && last.blockIndex === undefined
    && last.messageId === messageId

  if (last && (sameBlock || legacyAdjacent)) {
    segments[segments.length - 1] = { ...last, content: last.content + content }
  } else {
    segments.push({ kind, content, ...(messageId ? { messageId } : {}), ...(blockIndex !== undefined ? { blockIndex } : {}) })
  }

  return { ...current, segments }
}

function appendCompletedBlocks(current: NativeTurnStreamState, event: NativeEvent): NativeTurnStreamState {
  if (!Array.isArray(event.content_blocks) || event.content_blocks.length === 0) return current
  const messageId = event.message_id || current.activeStreamMessageId || undefined
  const observed = current.segments.slice(current.activeSegmentStart)
  let next = current
  for (const block of event.content_blocks) {
    if (!block || (block.kind !== 'thinking' && block.kind !== 'text') || !block.text) continue
    const blockIndex = typeof block.index === 'number' ? block.index : undefined
    const alreadyObserved = observed.some((segment) => {
      if (segment.kind !== block.kind) return false
      if (messageId && segment.messageId && segment.messageId !== messageId) return false
      if (blockIndex !== undefined && segment.blockIndex !== undefined && segment.blockIndex !== blockIndex) return false
      return true
    })
    if (alreadyObserved) continue
    next = appendSegment(next, block.kind, block.text, { messageId, blockIndex })
    if (block.kind === 'thinking') next = { ...next, thinking: next.thinking + block.text }
  }
  return next
}

function recentSegmentMatches(
  current: NativeTurnStreamState,
  kind: NativeContentSegment['kind'],
  messageId?: string,
): boolean {
  return current.segments.slice(current.activeSegmentStart).some((segment) =>
    segment.kind === kind && (!messageId || !segment.messageId || segment.messageId === messageId),
  )
}

function clearCompletedReplay(current: NativeTurnStreamState): NativeTurnStreamState {
  return {
    ...current,
    completedTextAwaitingDelta: '',
    completedReplayProbe: '',
    completedReplayMessageId: '',
    completedReplayProbeBlockIndex: null,
    completedReplayProbeStreamMessageId: '',
  }
}

function appendAcceptedText(
  current: NativeTurnStreamState,
  content: string,
  event: NativeEvent,
): NativeTurnStreamState {
  const withText = {
    ...current,
    text: current.text + content,
    pendingText: current.pendingText + content,
  }
  return appendSegment(withText, 'text', content, {
    messageId: current.activeStreamMessageId || event.stream_message_id,
    blockIndex: typeof event.block_index === 'number' ? event.block_index : undefined,
  })
}

export function applyNativeStreamEvent(
  current: NativeTurnStreamState,
  event: NativeEvent,
): NativeTurnStreamState {
  if (event.stream_event_type === 'message_start') {
    const streamMessageId = event.stream_message_id || ''
    const base = current.completedReplayMessageId && streamMessageId && current.completedReplayMessageId !== streamMessageId
      ? clearCompletedReplay(current)
      : current
    return {
      ...base,
      activeStreamMessageId: streamMessageId,
      activeSegmentStart: base.segments.length,
    }
  }

  if (event.kind === 'thinking_delta') {
    const content = event.text || ''
    const next = { ...current, thinking: current.thinking + content }
    return appendSegment(next, 'thinking', content, {
      messageId: current.activeStreamMessageId,
      blockIndex: typeof event.block_index === 'number' ? event.block_index : undefined,
    })
  }

  if (event.kind === 'text_delta') {
    const chunk = event.text || ''
    if (current.completedTextAwaitingDelta && chunk) {
      const candidate = current.completedReplayProbe + chunk
      const expected = current.completedTextAwaitingDelta
      if (expected.startsWith(candidate) || candidate.startsWith(expected)) {
        return {
          ...current,
          completedReplayProbe: candidate,
          completedReplayProbeBlockIndex: typeof event.block_index === 'number' ? event.block_index : current.completedReplayProbeBlockIndex,
          completedReplayProbeStreamMessageId: current.activeStreamMessageId || current.completedReplayProbeStreamMessageId,
        }
      }
      const cleared = clearCompletedReplay(current)
      return appendAcceptedText(cleared, candidate, event)
    }
    return appendAcceptedText(current, chunk, event)
  }

  if (event.kind === 'user_replay') {
    const cleared = clearCompletedReplay(current)
    return { ...cleared, activeStreamMessageId: '', activeSegmentStart: cleared.segments.length }
  }

  if (event.kind === 'assistant' || event.kind === 'tool_use') {
    if (event.message_id && current.completedMessageIds.includes(event.message_id)) {
      if (event.message_id !== current.completedReplayMessageId) return current
      return clearCompletedReplay(current)
    }

    let next = appendCompletedBlocks(current, event)
    if (event.text) {
      if (!recentSegmentMatches(next, 'text', event.message_id || current.activeStreamMessageId || undefined)) {
        next = appendSegment(next, 'text', event.text, { messageId: event.message_id || current.activeStreamMessageId || undefined })
      }
      const hadPending = Boolean(next.pendingText && next.text.endsWith(next.pendingText))
      const base = hadPending ? next.text.slice(0, -next.pendingText.length) : next.text
      next = clearCompletedReplay({
        ...next,
        text: base + event.text,
        pendingText: '',
        completedTextAwaitingDelta: hadPending ? '' : event.text,
        completedReplayMessageId: hadPending ? '' : (event.message_id || ''),
      })
      // clearCompletedReplay intentionally clears completedReplayMessageId, so
      // restore the completed-first replay identity when needed.
      if (!hadPending) next = { ...next, completedTextAwaitingDelta: event.text, completedReplayMessageId: event.message_id || '' }
    }
    if (event.message_id) {
      next = {
        ...next,
        completedMessageIds: [...next.completedMessageIds, event.message_id],
      }
    }
    return { ...next, activeStreamMessageId: '', activeSegmentStart: next.segments.length }
  }

  if (event.kind === 'result' && event.text) {
    if (current.completedTextAwaitingDelta) {
      const laterFinal = current.completedReplayProbe && event.text !== current.completedTextAwaitingDelta
        ? event.text
        : ''
      let next = clearCompletedReplay({ ...current, pendingText: '' })
      if (laterFinal) next = appendAcceptedText(next, laterFinal, event)
      return next
    }
    if (!current.text) {
      return appendSegment({ ...current, text: event.text, pendingText: '' }, 'text', event.text)
    }
  }

  if (event.kind === 'done' && current.completedReplayProbe) {
    const probeEvent: NativeEvent = {
      kind: 'text_delta',
      block_index: current.completedReplayProbeBlockIndex ?? undefined,
      stream_message_id: current.completedReplayProbeStreamMessageId || undefined,
    }
    return appendAcceptedText(clearCompletedReplay({ ...current, pendingText: '' }), current.completedReplayProbe, probeEvent)
  }

  if (event.kind === 'subcon_context' && event.text) {
    const sequence = typeof event.sequence === 'number' ? event.sequence : undefined
    if (sequence !== undefined && current.recall.some((item) => item.sequence === sequence)) return current
    return {
      ...current,
      recall: [...current.recall, {
        text: event.text,
        phase: event.phase || 'user_prompt',
        sequence,
      }],
    }
  }
  return current
}
